import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import {
  CLOSURE_CONDITION_LABELS,
  CLOSURE_REASON_LABELS,
  LIGHTNING_CAVEAT,
  LIGHTNING_RETENTION_MIN,
  type Alert,
  type AlertEvaluation,
  type Asset,
  type PublicMapAlert,
  type PublicMapAsset,
  type PublicMapClosure,
  type PublicMapView,
  type RoadClosure,
  type TenantAlert,
} from '@ww/shared';
import { ApiError, api } from '../api';
import WeatherMap from '../components/WeatherMap';
import { DISCLAIMER, Logo } from '../components/HeaderBits';
import { fmtDateTime } from '../lib/time';

const REFRESH_MS = 60_000;

const box: CSSProperties = {
  padding: '14px 18px',
  background: '#0f1a2d',
  border: '1px solid #1e2c46',
  borderRadius: 10,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

/*
 * Adapters, one per layer.
 *
 * Same reasoning as PublicAlert's `mapAlertFrom`: WeatherMap only ever reads a handful
 * of fields off each shape, so the public payload is widened to the internal type with
 * inert placeholders and nothing else. Nothing the public endpoint did not return can
 * reach the map — in particular an asset here has a name, a type and a point, and the
 * radii, rules, groups, tags and internal id simply do not exist on this page.
 */

function mapAlertFrom(a: PublicMapAlert, i: number): TenantAlert {
  const id = `pm-alert-${i}`;
  const alert = {
    _id: id,
    nwsId: id,
    event: a.event,
    category: a.category,
    severity: '',
    headline: a.headline,
    geometry: a.geometry,
    areaDesc: '',
    expires: a.expires,
    sent: null,
    active: true,
    motion: null,
    affectedZones: [],
    parameters: {},
  } as unknown as Alert;
  const evaluation = {
    alertId: id,
    event: a.event,
    category: a.category,
    affectedAssets: [],
    qualifies: true,
    state: 'active',
  } as unknown as AlertEvaluation;
  return { alert, evaluation };
}

function mapAssetFrom(a: PublicMapAsset, i: number): Asset {
  return {
    _id: `pm-asset-${i}`,
    name: a.name,
    type: a.type,
    location: a.location,
    enabled: true,
    tags: [],
    notificationGroupIds: [],
    ruleOverrides: {},
  } as unknown as Asset;
}

function mapClosureFrom(c: PublicMapClosure, i: number): RoadClosure {
  return {
    _id: `pm-closure-${i}`,
    roadName: c.roadName,
    reason: c.reason,
    condition: c.condition,
    geometry: c.geometry,
    notes: '',
    state: 'active',
    nearAssets: [],
    notifyGroupIds: [],
    expectedReopenAt: c.expectedReopenAt,
    createdAt: c.since,
    updatedAt: c.since,
  } as unknown as RoadClosure;
}

/** "closed 3 hours" — the question a driver actually has about a barricade. */
function sinceLabel(iso: string, now: number): string {
  const mins = Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));
  if (!Number.isFinite(mins)) return 'time unknown';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'}`;
  const h = Math.round(mins / 60);
  if (h < 48) return `${h} hour${h === 1 ? '' : 's'}`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'}`;
}

/** Labels the public reads, not the codebase's terms. */
function reasonLabel(r: string): string {
  return CLOSURE_REASON_LABELS[r as keyof typeof CLOSURE_REASON_LABELS] ?? r;
}
function conditionLabel(c: string): string {
  return CLOSURE_CONDITION_LABELS[c as keyof typeof CLOSURE_CONDITION_LABELS] ?? c;
}

export default function PublicMap() {
  const { code = '' } = useParams();
  const [view, setView] = useState<PublicMapView | null>(null);
  const [error, setError] = useState<{ msg: string; status: number } | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const v = await api.publicMap(code);
        if (!cancelled) {
          setView(v);
          setError(null);
          setNow(Date.now());
        }
      } catch (e) {
        // A network blip on a page that already has a view is not worth blanking it:
        // a stale map with an honest "last updated" beats an error where the map was.
        if (!cancelled && !(e instanceof ApiError && e.status === 0 && view)) {
          setError({
            msg: e instanceof Error ? e.message : String(e),
            status: e instanceof ApiError ? e.status : 0,
          });
        }
      }
    };
    void load();
    // No SSE without a session, so this is the only thing keeping the page current.
    const timer = setInterval(() => void load(), REFRESH_MS);
    const tick = setInterval(() => setNow(Date.now()), 30_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
      clearInterval(tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const mapAlerts = useMemo(() => (view ? view.alerts.map(mapAlertFrom) : []), [view]);
  const mapAssets = useMemo(() => (view ? view.assets.map(mapAssetFrom) : []), [view]);
  const mapClosures = useMemo(() => (view ? view.closures.map(mapClosureFrom) : []), [view]);
  const mapTenant = useMemo(
    () => (view ? { center: view.center, boundary: view.boundary, timezone: view.timezone } : null),
    [view],
  );

  const frame = (children: React.ReactNode) => (
    <div style={{ minHeight: '100vh', background: 'radial-gradient(ellipse at top, #0f1d36 0%, #070d18 60%)' }}>
      <div
        style={{
          maxWidth: 900,
          margin: '0 auto',
          padding: '20px 16px 40px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <Logo />
        {children}
        <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.45, color: '#93a5c1', textAlign: 'center' }}>{DISCLAIMER}</p>
      </div>
    </div>
  );

  if (error && !view) {
    /*
     * A revoked link, a code that never existed and a typo all land here with the same
     * words on purpose. Saying "this link was revoked" would confirm the code was real,
     * which is the one thing someone guessing codes wants to learn.
     */
    return frame(
      error.status === 404 || error.status === 403 || error.status === 410 ? (
        <div style={{ ...box, alignItems: 'center', textAlign: 'center', padding: '28px 18px', gap: 10 }}>
          <div style={{ fontSize: 20, fontWeight: 600 }}>This link is no longer active</div>
          <div style={{ fontSize: 14.5, lineHeight: 1.55, color: '#c2cedf', maxWidth: 460 }}>
            Check with your county for the current map link. For official warnings, see weather.gov.
          </div>
        </div>
      ) : (
        <div className="err-box">Could not load this map: {error.msg}</div>
      ),
    );
  }
  if (!view) return frame(<div className="empty">Loading map…</div>);

  const tz = view.timezone;
  const closures = view.closures;

  return frame(
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h1 className="cond" style={{ margin: 0, fontSize: 30, fontWeight: 700, lineHeight: 1.1 }}>
          {view.county}
        </h1>
        <span style={{ fontSize: 14, color: '#9fb0c8' }}>
          Weather warnings and road conditions
        </span>
      </div>

      <div
        style={{
          position: 'relative',
          height: 'min(58vh, 520px)',
          minHeight: 320,
          background: '#0b1526',
          border: '1px solid #1e2c46',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <WeatherMap
          tenant={mapTenant}
          alerts={mapAlerts}
          assets={mapAssets}
          closures={mapClosures}
          lightning={view.lightning}
          showRadar={false}
          noAlertNav
          compact
        />
      </div>

      {/* Last updated, stated outright: a tab left open overnight otherwise looks live. */}
      <div style={{ fontSize: 12.5, color: '#93a5c1' }}>
        Last updated {fmtDateTime(view.generatedAt, tz)} · refreshes every minute while this page is open.
      </div>

      {view.alerts.length > 0 && (
        <div style={box}>
          <span className="lbl">Weather warnings in effect</span>
          {view.alerts.map((a, i) => (
            <div key={`${a.event}-${i}`} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 15.5, fontWeight: 600 }}>{a.event}</span>
              <span style={{ fontSize: 13.5, lineHeight: 1.5, color: '#c2cedf' }}>
                {a.headline || 'Issued by the National Weather Service.'}
              </span>
              {a.expires && (
                <span style={{ fontSize: 12.5, color: '#93a5c1' }}>
                  In effect until {fmtDateTime(a.expires, tz)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={box}>
        <span className="lbl">Road closures</span>
        {closures.length === 0 ? (
          <span style={{ fontSize: 15, color: '#c2cedf' }}>
            No roads are reported closed right now.
          </span>
        ) : (
          closures.map((c, i) => (
            <div
              key={`${c.roadName}-${i}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                paddingTop: i === 0 ? 0 : 8,
                borderTop: i === 0 ? 'none' : '1px solid #1e2c46',
              }}
            >
              <span style={{ fontSize: 15.5, fontWeight: 600 }}>{c.roadName}</span>
              <span style={{ fontSize: 13.5, color: '#c2cedf' }}>
                {reasonLabel(c.reason)} · {conditionLabel(c.condition)}
              </span>
              <span style={{ fontSize: 12.5, color: '#93a5c1' }}>
                Closed {sinceLabel(c.since, now)} · since {fmtDateTime(c.since, tz)}
                {c.expectedReopenAt ? ` · expected to reopen ${fmtDateTime(c.expectedReopenAt, tz)}` : ''}
              </span>
            </div>
          ))
        )}
      </div>

      {view.assets.length > 0 && (
        /* Only when there is something to say. A county that has published no assets
           should not be told about a feature it is not using. */
        <div style={box}>
          <span className="lbl">Locations shown on the map</span>
          <span style={{ fontSize: 15, lineHeight: 1.6 }}>{view.assets.map((a) => a.name).join(' · ')}</span>
        </div>
      )}

      {view.lightning.length > 0 && (
        /*
         * The full caveat, spelled out, exactly as the public alert page does it. This
         * page is read by people with no training, on a phone, possibly while deciding
         * whether it is safe to be outside. The one reading that must not survive this
         * paragraph is "lightning struck where that dot is".
         */
        <div style={{ ...box, gap: 6 }}>
          <span className="lbl" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="ltg-dot" aria-hidden /> Lightning nearby
          </span>
          <span style={{ fontSize: 15, lineHeight: 1.5 }}>
            {view.lightning.length} flash{view.lightning.length === 1 ? '' : 'es'} detected in this area
            in the last {LIGHTNING_RETENTION_MIN} minutes.
          </span>
          <span style={{ fontSize: 13, lineHeight: 1.5, color: '#9fb0c8' }}>{LIGHTNING_CAVEAT}</span>
          <span style={{ fontSize: 13, lineHeight: 1.5, color: '#9fb0c8' }}>
            In plain words: these marks come from a weather satellite that sees lightning from orbit
            to within about 6 miles, and they include flashes inside the clouds that never reached
            the ground — <strong>they are not the exact spots where lightning struck</strong>. Treat
            any lightning in the area as a reason to get inside.
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <a
          href="https://www.weather.gov"
          target="_blank"
          rel="noreferrer"
          className="btn btn-secondary btn-sm hov"
          style={{ textDecoration: 'none' }}
        >
          Official NWS warnings
        </a>
      </div>
    </>,
  );
}
